import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import "./EditDraft.scss";
import { useParams } from "react-router-dom";
import ApiContext from "../../../../Api/ApiContext";
import { IClause, IDraft } from "../../Interfaces";
import Clauses from "../../Components/Clauses/Clauses";
import { ReactSVG } from "react-svg";
import messageIcon from '../../../../Assets/icons/chat-circle-text.svg';
import undoIcon from '../../../../Assets/icons/undo.svg';
import Copilot from "../../../Copilot/Copilot";
import MultiSwitch from "../../../../Components/MultiSwitch/MultiSwitch";
import Grid from "../../Components/Grid/Grid";
import DraftingHeader, { EDraftingHeaderSource } from "../../Components/Header/DraftingHeader";
import {draftingSlice} from "../../../../Store/draftingSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../../Store/store";
import Loading from "../../../../Components/Loading/Loading";
import { useTranslation } from "react-i18next";
import { selectResellerConfig } from "../../../../Store/configSlice";
import CreateDraft from "../CreateDraft/CreateDraft";
import AccessDeniedScreen from "../../../../Components/Misc/AccessDeniedScreen";
import Modal from "../../../../Components/Modal/Modal";
import DownloadButton, { EDownloadSource } from "../../Components/Download/DownloadButton";
import { EDownloadFileExtention } from "../../Components/Download/DownloadApiCall";
import Button, { EButtonType } from "../../../../Components/Buttons/Button";

const EditDraft = () => {

    const resellerConfig = useSelector(selectResellerConfig);

    const params = useParams();
    const urlId = params.id;

    const isReseller = process.env.REACT_APP_IS_RESELLER === 'true';

    const id = (() => {
            if (isReseller) {
                if (resellerConfig?.legalFlyId) {
                return resellerConfig.legalFlyId.toString();
            } else {
                return null;
            }
        }
        return urlId;
    })();

    interface ChangedClause {
        clause: IClause;
        sequence: number;
        action: 'add' | 'change' | 'move' | 'delete';
    }

    const [ undoing, setUndoing ] = useState<boolean>(false);
    const [ undoClauses, setUndoClauses ] = useState<ChangedClause[]>([]);

    const undo = () => {
        if (undoClauses.length === 0) return;
        setUndoing(true);
        const { clause, sequence, action } = undoClauses[undoClauses.length - 1];
        switch (action) {
            case 'add':
                api.delete(`/drafts/${draft.id}/clauses/${clause.id}`).then(() => {
                    fetchDraft();
                    setUndoClauses(undoClauses.slice(0, undoClauses.length - 1));
                    setUndoing(false);
                })
                break;
            case 'change':
                api.put(`/drafts/${draft.id}/clauses/${clause.id}`, {
                    content: clause.content,
                    sequence: clause.sequence,
                    title: clause.title,
                    instructions: clause.instructions,
                    instructions_complete: clause.instructions_complete
                }).then(() => {
                    fetchDraft();
                    setUndoClauses(undoClauses.slice(0, undoClauses.length - 1));
                    setUndoing(false);
                });
                break;
            case 'move':
                const updatedItems = [...clauses];
                const index = updatedItems.findIndex(c => c.id === clause.id);
                updatedItems.splice(index, 1);
                updatedItems.splice(sequence, 0, clause);
                setClauses(updatedItems);
                api.put(`/drafts/${id}/clauses/sequence`, {
                    order: updatedItems.map((clause, index) => ({ clauseId: clause.id, sequence: index }))
                }).then(() => {
                    fetchDraft();
                    setUndoClauses(undoClauses.slice(0, undoClauses.length - 1));
                    setUndoing(false);
                });
                break;
            case 'delete':
                api.post(`/drafts/${id}/clauses`, {
                    content: clause.content,
                    sequence: clause.sequence,
                    title: clause.title
                }).then(() => {
                    fetchDraft();
                    setUndoClauses(undoClauses.slice(0, undoClauses.length - 1));
                    setUndoing(false);
                });
                break;
        }
    };
    
    const [ draft, setDraft ] = useState<IDraft>(null);
    const [ clauses, setClauses ] = useState<IClause[]>([]);
    const [ tab, setTab ] = useState<number>(0);
    const [ areHiddenClausesVisible, setHiddenClausesVisible ] = useState<boolean>(false);

    const api = useContext(ApiContext);
    const { t } = useTranslation();

    const instructionsRequired = useMemo(() => clauses.some(clause => clause.instructions?.length > 0 && !clause.instructions_complete), [clauses]);

    const dispatch = useDispatch<AppDispatch>();

    const fetchDraft = useCallback(() => {
        api.get('/drafts/' + id).then(res => {
            setDraft(res.data.draft);
            setClauses(res.data.clauses);
        });
    }, [id, api]);

    useEffect(() => {
        dispatch(draftingSlice.actions.setRedraftingClause(null));
        if(id) fetchDraft();
    }, [fetchDraft, dispatch]);

    const onSetTap = (option: number) => setTab(option)

    const updateDraftName = (name: string) => api.put(`/drafts/${draft.id}`, { name })

    const updateDraftSequence = (result: any) => {
        if (!result.destination) {
            return;
        }
    
        const updatedItems = [...clauses];
        const [reorderedItem] = updatedItems.splice(result.source.index, 1);
        updatedItems.splice(result.destination.index, 0, reorderedItem);
    
        setClauses(updatedItems);

        const seqOrder = updatedItems.map((clause, index) => ({ clauseId: clause.id, sequence: index }));
        api.put(`/drafts/${draft.id}/clauses/sequence`, {
            order: [ ...seqOrder ]
        }).then(() => {
            if(!instructionsRequired)
                setUndoClauses(
                    [
                        ...undoClauses.slice(-9),
                        { clause: reorderedItem, sequence: reorderedItem.sequence, action: 'move' }
                    ]
                );
            fetchDraft();
        });
    }

    const updateClause = (clause: IClause) => {

        const index = clauses.findIndex(c => c.sequence === clause.sequence);
        if(!instructionsRequired)
            setUndoClauses(
                [
                    ...undoClauses.slice(-9),
                    { clause: clauses[index], sequence: clauses[index].sequence, action: 'change' }
                ]
            );

        api.put(`/drafts/${draft.id}/clauses/${clause.id}`, {
            content: clause.content,
            sequence: clause.sequence,
            title: clause.title,
            instructions: clause.instructions,
            instructions_complete: clause.instructions_complete
        }).then(() => {
            fetchDraft();
        });
    }

    const updateClauseBySequence = (sequence: number, clause: IClause) => {

        const index = clauses.findIndex(c => c.sequence === sequence);
        if(!instructionsRequired)
            setUndoClauses(
                [
                    ...undoClauses.slice(-9),
                    { clause: clauses[index], sequence: clauses[index].sequence, action: 'change' }
                ]
            );

        setClauses([
            ...clauses.slice(0, index),
            clause,
            ...clauses.slice(index + 1)
        ]);

        api.put(`/drafts/${draft.id}/clauses/${clause.id}`, {
            content: clause.content,
            sequence: clause.sequence,
            title: clause.title,
            instructions: clause.instructions,
            instructions_complete: clause.instructions_complete
        });
    }

    const addNewClauseAtSequence = (sequence: number) => {
        const seq = sequence;

        const newClause: IClause = {
            id: null,
            draft_id: draft.id,
            content: t('drafting.newClauseContent'),
            sequence: seq,
            title: t('drafting.newClauseTitle')
        };

        setClauses([
            ...clauses.slice(0, seq),
            newClause,
            ...clauses.slice(seq)
        ]);

        api.post(`/drafts/${draft.id}/clauses`, {
            content: newClause.content,
            sequence: newClause.sequence + 1,
            title: newClause.title
        }).then((res) => {
            if(!instructionsRequired)
                setUndoClauses(
                    [
                        ...undoClauses.slice(-9),
                        { clause: res.data.clause, sequence: res.data.clause.sequence, action: 'add' }
                    ]
                );
            fetchDraft();
        });
    }

    const deleteClauseById = (clauseId: number) => {
        const updatedClauses = clauses.filter(clause => clause.id !== clauseId);
        setClauses(updatedClauses);
        api.delete(`/drafts/${draft.id}/clauses/${clauseId}`).then(() => {

            if(!instructionsRequired)
                setUndoClauses(
                    [
                        ...undoClauses.slice(-9),
                        { clause: clauses.find(c => c.id === clauseId), sequence: (clauses.find(c => c.id === clauseId)).sequence, action: 'delete' }
                    ]
                );
            fetchDraft();
        });
    }

    useEffect(() => {
        // Set the first clause with instructions_complete = false as the redrafting clause
        const redraftingClause = clauses.find((element) => (element?.instructions?.length > 0 && element.instructions_complete === false));
        dispatch(draftingSlice.actions.setRedraftingClause(redraftingClause));
    }, [clauses, dispatch]);

    const [ showFinishedModal, setShowFinishedModal ] = useState<boolean>(false);

    const prevInstructionsRequired = useRef(instructionsRequired);

    // Show modal when instructionsRequired changes from true to false
    useEffect(() => {
        
        if (prevInstructionsRequired.current === true && instructionsRequired === false) {
            setShowFinishedModal(true);
        }
        
        prevInstructionsRequired.current = instructionsRequired;
    }, [instructionsRequired]);

    if (resellerConfig) {
        if (!resellerConfig.access) {
            return <AccessDeniedScreen />
        } else if (!resellerConfig.legalFlyId) {
            return <CreateDraft />
        }
    }

    if (!draft) return <Loading />;

    const {
        name,
        type,
        jurisdiction
    } = draft;

    const FinishedModal = () => {
        
        return (
            <Modal
                showModal={showFinishedModal}
                setShowModal={setShowFinishedModal}
                title={t('drafting.finishedModalTitle')}
                content={<div className="draft-finished-modal-content">
                    <p>{t('drafting.finishedModalMessage')}</p>
                    <div className="download-buttons">
                        <DownloadButton downloadId={id} filename={name} extension={EDownloadFileExtention.PDF} source={EDownloadSource.DRAFT}/>
                        <DownloadButton downloadId={id} filename={name} extension={EDownloadFileExtention.WORD} source={EDownloadSource.DRAFT}/>
                    </div>
                    <p>{t('drafting.finishedModalMessage2')}</p>
                    <Button text={t('drafting.finishedModalButton')} buttonType={EButtonType.SECONDARY} onClick={() => setShowFinishedModal(false)} />
                </div>}
                confetti={true}
            />
        );
    }

    return (
        <div className={`edit-draft ${instructionsRequired ? 'instructions-required' : ''}`}>
            {showFinishedModal && <FinishedModal />}
            <Grid
                leftHeader={
                    <DraftingHeader
                        id={id}
                        name={name}
                        type={type}
                        jurisdiction={jurisdiction}
                        source={EDraftingHeaderSource.DRAFT}
                        updateName={updateDraftName}
                        areHiddenClausesVisible={areHiddenClausesVisible}
                        setHiddenClausesVisible={setHiddenClausesVisible}
                    />
                }
                leftContent={
                    <div className="clause-container">
                        {undoClauses.length > 0 && <div className="undo" onClick={undo}>
                            { undoing ? <div className="loader"></div> : <ReactSVG src={undoIcon} /> }
                        </div>}
                        <Clauses
                            isDrafting
                            clauses={clauses}
                            setClauses={setClauses}
                            documentId={draft.id}
                            updateSequence={updateDraftSequence}
                            addNewClauseAtSequence={addNewClauseAtSequence}
                            updateClauseBySequence={updateClauseBySequence}
                            deleteClauseById={deleteClauseById}
                        />
                    </div>
                }
                rightHeader={
                        <MultiSwitch 
                            options={[
                                <><ReactSVG src={messageIcon} />{t('drafting.copilot')}</>
                            ]}
                            defaultOption={tab}
                            setOption={onSetTap}
                            type="tabs"
                        />
                }
                rightContent={
                    <>
                        { tab === 0 && <Copilot sourceType="draft" sourceId={draft?.id} clauses={clauses} updateContent={updateClause} />
                        }
                    </>
                }
            />
        </div>
    )
}

export default EditDraft;